import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import type { ChartConfig, StatCardConfig } from "@hooks/use-reports/types";

export interface UpdateUserGeneratedReportPayload {
  title: string;
  description?: string;
  /** Display order in the report navigation tabs (decimals for user reports, e.g. 2.01) */
  sort_order?: number;
  admin_config?: {
    dataSources: Array<{
      id: string;
      dbTable: string;
      groupBy: string[];
      sum?: string[];
    }>;
    charts?: ChartConfig[];
    statCards?: StatCardConfig[];
  };
  report_filters?: string[];
}

interface UpdateUserGeneratedReportResponse {
  id?: string;
  title?: string;
  description?: string;
  success?: boolean;
}

/**
 * Hook to update the title/description of a user-generated report.
 * Calls: PUT /wp-json/smassets/v1/reports/{reportId}/user-generated
 */
export const useUpdateUserGeneratedReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const updateReport = useCallback(
    async (
      reportId: string,
      payload: UpdateUserGeneratedReportPayload
    ): Promise<UpdateUserGeneratedReportResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl(`wp-json/smassets/v1/reports/${reportId}/user-generated`);

        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(`Failed to update report: ${response.status}`);
        }

        const result = await response.json();
        setLoading(false);
        return result as UpdateUserGeneratedReportResponse;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update report";
        setError(errorMessage);
        setLoading(false);
        return null;
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  return { updateReport, loading, error };
};
